import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import authService from '../services/auth';
import axiosInstance from '../services/axiosConfig';

// Remove the module-level variable to prevent caching
// let authInitialized = false;

const AuthContext = createContext(null);

// Define public paths that don't require authentication
const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password', '/news', '/faq'];

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isAffiliate, setIsAffiliate] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const isMounted = useRef(true);
    const initialAuthCheckDone = useRef(false);
    
    const checkIsPublicPath = useCallback((path) => {
        return PUBLIC_PATHS.some(publicPath => 
            path === publicPath || path.startsWith(`${publicPath}/`)
        );
    }, []);
    
    // This function should run to initialize auth
    const initializeAuth = useCallback(async () => {
        if (initialAuthCheckDone.current) return;
        
        try {
            const profile = await authService.getProfile();
            
            if (!isMounted.current) return;
            
            if (profile) {
                setUser({
                    ...profile,
                    is_admin: profile.role === 'admin'
                });
                setIsAuthenticated(true);
                
                try {
                    const affiliateResponse = await axiosInstance.get('/affiliate/status');
                    if (!isMounted.current) return;
                    setIsAffiliate(affiliateResponse.data.is_affiliate);
                } catch (err) {
                    if (!isMounted.current) return;
                    setIsAffiliate(false);
                }
            } else {
                setUser(null);
                setIsAuthenticated(false);
                setIsAffiliate(false);
            }
        } catch (error) {
            console.error('Auth initialization error:', error);
            if (!isMounted.current) return;
            setUser(null);
            setIsAuthenticated(false);
            setIsAffiliate(false);
        } finally {
            if (isMounted.current) {
                initialAuthCheckDone.current = true;
                setLoading(false);
            }
        }
    }, []);

    // Effect to handle protected route access - completely separate from auth initialization
    useEffect(() => {
        const isPublicPath = checkIsPublicPath(location.pathname);
        
        // Only redirect if:
        // 1. Initial auth check is complete
        // 2. Not currently loading
        // 3. User is not authenticated
        // 4. Current path is not public
        // 5. Not already on the login page
        if (initialAuthCheckDone.current && 
            !loading && 
            !isAuthenticated && 
            !isPublicPath && 
            location.pathname !== '/login') {
            history.replace('/login', { from: location.pathname });
        }
    }, [loading, isAuthenticated, location.pathname, checkIsPublicPath, history]);

    // Initialize auth only once on mount
    useEffect(() => {
        initializeAuth();
        return () => {
            isMounted.current = false;
        };
    }, [initializeAuth]);

    const refreshAuth = useCallback(async () => {
        try {
            const profile = await authService.getProfile();
            
            if (!isMounted.current) return;
            
            if (profile) {
                setUser({
                    ...profile,
                    is_admin: profile.role === 'admin'
                });
                setIsAuthenticated(true);
                
                try {
                    const affiliateResponse = await axiosInstance.get('/affiliate/status');
                    if (!isMounted.current) return;
                    setIsAffiliate(affiliateResponse.data.is_affiliate);
                } catch (err) {
                    if (!isMounted.current) return;
                    setIsAffiliate(false);
                }
            } else {
                setUser(null);
                setIsAuthenticated(false);
                setIsAffiliate(false);
            }
        } catch (error) {
            console.error('Auth refresh error:', error);
            if (!isMounted.current) return;
            setUser(null);
            setIsAuthenticated(false);
            setIsAffiliate(false);
        }
    }, []);

    const login = async (username, password, rememberMe = true) => {
        try {
            const response = await authService.login(username, password, rememberMe);
            
            if (!response) {
                throw new Error('No response received from login');
            }
            
            // Set user data from response
            setUser({
                ...response,
                is_admin: response.role === 'admin'
            });
            setIsAuthenticated(true);
            
            // Check affiliate status
            try {
                const affiliateResponse = await axiosInstance.get('/affiliate/status');
                if (isMounted.current) {
                    setIsAffiliate(affiliateResponse.data.is_affiliate);
                }
            } catch (err) {
                console.error('Error checking affiliate status:', err);
                if (isMounted.current) {
                    setIsAffiliate(false);
                }
            }
            
            return response;
        } catch (error) {
            console.error('Login error:', error);
            if (isMounted.current) {
                setUser(null);
                setIsAuthenticated(false);
                setIsAffiliate(false);
            }
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clear local state regardless of server response
            if (isMounted.current) {
                setUser(null);
                setIsAuthenticated(false);
                setIsAffiliate(false);
                history.push('/');
            }
        }
    };

    const register = async (username, email, password) => {
        const response = await authService.register(username, email, password);
        
        if (isMounted.current) {
            setUser({
                ...response,
                is_admin: response.role === 'admin'
            });
            setIsAuthenticated(true);
        }
    };

    const updateProfile = async (data) => {
        try {
            const response = await authService.updateProfile(data);
            
            // Update the user state immediately with the new profile data
            if (isMounted.current) {
                setUser(prevUser => ({
                    ...prevUser,
                    ...response,
                    is_admin: response.role === 'admin'
                }));
            }
            
            return response;
        } catch (error) {
            console.error('Profile update error:', error);
            throw error;
        }
    };

    const changePassword = async (currentPassword, newPassword) => {
        try {
            const response = await authService.changePassword(currentPassword, newPassword);
            return response;
        } catch (error) {
            console.error('Password change error:', error);
            throw error;
        }
    };

    const getApiKey = async () => {
        try {
            const response = await authService.getApiKey();
            return response;
        } catch (error) {
            console.error('Get API key error:', error);
            throw error;
        }
    };

    const generateApiKey = async () => {
        try {
            const response = await authService.generateApiKey();
            if (isMounted.current) {
                setUser(prevUser => ({
                    ...prevUser,
                    has_api_key: true,
                    api_key_created_at: response.created_at
                }));
            }
            return response;
        } catch (error) {
            console.error('Generate API key error:', error);
            throw error;
        }
    };

    const revokeApiKey = async () => {
        try {
            const response = await authService.revokeApiKey();
            if (isMounted.current) {
                setUser(prevUser => ({
                    ...prevUser,
                    has_api_key: false,
                    api_key_created_at: null
                }));
            }
            return response;
        } catch (error) {
            console.error('Revoke API key error:', error);
            throw error;
        }
    };

    const value = {
        user,
        isAuthenticated,
        isAffiliate,
        loading,
        login,
        logout,
        register,
        updateProfile,
        changePassword,
        refreshAuth,
        getApiKey,
        generateApiKey,
        revokeApiKey
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext; 